/**
 * Enum Constants for Subscription Module
 * Centralized management of all enum values and union types to avoid typos and ensure consistency
 */

// Plan Types
export const PLAN_TYPES = {
  STARTER: 'starter',
  STANDARD: 'standard',
  PRO: 'pro',
} as const;

export type PlanType = (typeof PLAN_TYPES)[keyof typeof PLAN_TYPES];

// Plan Types Name
export const PLAN_TYPE_NAMES = {
  [PLAN_TYPES.STARTER]: 'Starter',
  [PLAN_TYPES.STANDARD]: 'Standard',
  [PLAN_TYPES.PRO]: 'Pro',
};

// Billing Cycles
export const BILLING_CYCLES = {
  MONTHLY: 'monthly',
  ANNUAL: 'annual',
} as const;

export type BillingCycle = (typeof BILLING_CYCLES)[keyof typeof BILLING_CYCLES];

export const BILLING_CYCLE_NAMES = {
  [BILLING_CYCLES.MONTHLY]: 'Monthly',
  [BILLING_CYCLES.ANNUAL]: 'Annual',
};

// Subscription Status
export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  TERMINATED: 'terminated',
  UPCOMING: 'upcoming',
  INACTIVE: 'inactive',
  NO_SUBSCRIPTION: 'no_subscription',
  TRIAL: 'trial',
  TRIAL_EXPIRED: 'trial_expired',
  SUSPENDED: 'suspended',
  SUSPENDED_DUE: 'suspended_due',
  FAILED_PAYMENT: 'failed_payment',

  ACTIVE_CANCELLED: 'active_cancelled',
  ACTIVE_UPCOMING: 'active_upcoming',
  CANCELLED: 'cancelled',
  PROCESSING: 'processing',
} as const;

export type SubscriptionStatus =
  (typeof SUBSCRIPTION_STATUS)[keyof typeof SUBSCRIPTION_STATUS];

export const SUSPENDED_DUE_PAYMENT_OPTIONS = {
  REACTIVATE: 'reactivate',
  CONTINUE_WITH_BALANCE: 'continue_with_balance',
  SELECT_NEW_PLAN: 'select_new_plan',
} as const;

export type SuspendedDuePaymentOption =
  (typeof SUSPENDED_DUE_PAYMENT_OPTIONS)[keyof typeof SUSPENDED_DUE_PAYMENT_OPTIONS];

// Payment Status
export const PAYMENT_STATUS = {
  PAID: 'paid',
  UNPAID: 'unpaid',
  FAILED_PAYMENT: 'failed_payment',
  OVERPAID: 'overpaid',
  OVERDUE: 'overdue',
  PARTLY_PAID: 'partly_paid',
} as const;

export const PAYMENT_STATUS_NAMES = {
  PAID: 'Paid',
  UNPAID: 'Unpaid',
  FAILED_PAYMENT: 'Failed Payment',
  OVERPAID: 'Overpaid',
  OVERDUE: 'Overdue',
  PARTLY_PAID: 'Partly Paid',
} as const;

export type PaymentStatus =
  (typeof PAYMENT_STATUS)[keyof typeof PAYMENT_STATUS];

export const INVOICE_STATUS = {
  ACTIVE: 'active',
  DRAFT: 'draft',
} as const;

export type InvoiceStatus =
  (typeof INVOICE_STATUS)[keyof typeof INVOICE_STATUS];

export const CAPTURE_STATUS = {
  APPROVED: 'A',
  RETRY: 'B',
  DECLINED: 'C',
  REJECT: 'R',
} as const;

export type CaptureStatus =
  (typeof CAPTURE_STATUS)[keyof typeof CAPTURE_STATUS];

// Feature Types
export const FEATURE_TYPES = {
  BASE_FEATURES: 'baseFeatures',
  SMS: 'sms',
  TELEHEALTH: 'telehealth',
  FAX: 'fax',
} as const;

export type FeatureType = (typeof FEATURE_TYPES)[keyof typeof FEATURE_TYPES];

// Add-on Types
export const ADDON_TYPES = {
  RCM: 'rcm',
} as const;

export type AddonType = (typeof ADDON_TYPES)[keyof typeof ADDON_TYPES];

// License Types
export const LICENSE_TYPES = {
  THERAPIST: 'therapist',
  NON_THERAPIST: 'nonTherapist',
} as const;

export type LicenseType = (typeof LICENSE_TYPES)[keyof typeof LICENSE_TYPES];

// Promotion Amount Types
export const PROMOTION_AMOUNT_TYPES = {
  PERCENTAGE: 'P',
  FIXED_AMOUNT: 'A',
} as const;

export type PromotionAmountType =
  (typeof PROMOTION_AMOUNT_TYPES)[keyof typeof PROMOTION_AMOUNT_TYPES];

export const PAYMENT_METHODS = {
  CC_KEYED: 'cc-keyed',
  CC_SWIPE: 'cc-swipe',
} as const;

export type PaymentMethod =
  (typeof PAYMENT_METHODS)[keyof typeof PAYMENT_METHODS];

export const ACTION_TYPES = {
  UPGRADE: 'upgrade',
  DOWNGRADE: 'downgrade',
  CANCEL_DOWNGRADE: 'cancel_downgrade',
  CANCEL_SUBSCRIPTION: 'cancel_subscription',
  RESUME: 'resume',
  START_NOW: 'start_now',
} as const;

export type ActionType = (typeof ACTION_TYPES)[keyof typeof ACTION_TYPES];

export const SUBSCRIPTION_DETAIL_COMPONENT = {
  CHARGES: 'charges',
  SUSPENDED_CHARGES: 'suspended_charges',
  SCHEDULED: 'scheduled',
  NEXT_TERM: 'next_term',
  STARTED: 'started',
  DISCOUNT: 'discount',
  PAYMENT_METHOD: 'payment_method',
  DUE_AMOUNT: 'due_amount',
  DUE_DATE: 'due_date',
  SUSPENDED: 'suspended',
  TERMINATED: 'terminated',
  CANCELLED: 'cancelled',
  ACTIVE_STATS: 'active_stats',
};

export type SubscriptionDetailComponent =
  (typeof SUBSCRIPTION_DETAIL_COMPONENT)[keyof typeof SUBSCRIPTION_DETAIL_COMPONENT];

export const SUBSCRIPTION_PLAN_SCREEN_PAGES = {
  PLAN_LIST: 'plan_list',
  CHOOSE_PLAN: 'choose_plan',
  PLAN_DETAILS: 'plan_details',
  PAYMENT_METHOD: 'payment_method',
  INVOICE_HISTORY: 'invoice_history',
} as const;

export type SubscriptionPlanScreenPage =
  (typeof SUBSCRIPTION_PLAN_SCREEN_PAGES)[keyof typeof SUBSCRIPTION_PLAN_SCREEN_PAGES];

export const SUBSCRIPTION_DETAIL_ACTION = {
  CANCEL_DOWNGRADE: 'cancel_downgrade',
  CANCEL_SUBSCRIPTION: 'cancel_subscription',
  RESUME: 'resume',
  START_NOW: 'start_now',
  CHANGE_PLAN: 'change_plan',
  UPDATE_PAYMENT_METHOD: 'update_payment_method',
} as const;

export type SubscriptionDetailAction =
  (typeof SUBSCRIPTION_DETAIL_ACTION)[keyof typeof SUBSCRIPTION_DETAIL_ACTION];

export const CARD_TYPES = {
  VISA: 'VISA',
  MASTERCARD: 'MC',
  AMERICAN_EXPRESS: 'AMEX',
  DISCOVER_OR_DINERS_CLUB: 'DISC',
  JCB: 'JCB',
} as const;
export type CardType = (typeof CARD_TYPES)[keyof typeof CARD_TYPES];

export const PLAN_CREATION_SOURCE = {
  // Initial subscription
  SUBSCRIBE_NEW: 'subscribe_new', // Client subscribe new plan

  // Manual actions by user/admin
  START_NOW: 'start_now', // Activate upcoming plan immediately

  // Auto system actions
  AUTO_UPGRADE: 'auto_upgrade', // PTE-9670: Sine this ticket, this is used for auto upgrade
  AUTO_UPGRADE_STARTER_TO_STANDARD: 'auto_upgrade_starter_to_standard', // PTE-9670: Auto upgrade from Starter to Standard (exceed the limit)
  AUTO_ACTIVATE_UPCOMING: 'auto_activate_upcoming', // Auto activate upcoming plan
  RECURRING_PAYMENT: 'recurring_payment', // Create new from recurring payment cronjob

  // Plan transitions
  UPGRADE: 'upgrade', // Manual upgrade
  DOWNGRADE: 'downgrade', // Manual downgrade (schedule)
  CANCEL_DOWNGRADE: 'cancel_downgrade', // Cancel downgrade (schedule)
};

export type PlanCreationSource =
  (typeof PLAN_CREATION_SOURCE)[keyof typeof PLAN_CREATION_SOURCE];
